import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

/**
 * Privacy Policy — App Store & Play Store compliance.
 *
 * Both stores block submissions without a live, in-app privacy policy.
 * This page is deliberately plain-English, palette-matched, and covers
 * every third party we hit (Stripe, Google Analytics, Clarity, PostHog,
 * Resend, Apple, Google). Last-updated date bumps on any material edit.
 */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main
        className="max-w-3xl mx-auto px-4 py-12 text-[var(--text-primary)]"
        data-testid="privacy-page"
      >
        <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-2 text-[#3B2416]">
          Privacy Policy
        </h1>
        <p className="text-sm text-[var(--text-muted)] mb-10">
          Last updated: February&nbsp;7,&nbsp;2026
        </p>

        <Section title="The short version">
          <p>
            Sudoku Academy is a sudoku puzzle site. We collect the bare minimum
            needed to run your account, save your progress, and process
            subscriptions. We do not sell your data. You can delete your
            account and everything tied to it at any time from the Dashboard.
          </p>
        </Section>

        <Section title="What we collect">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account:</strong> username, optional email, hashed password.</li>
            <li><strong>Gameplay:</strong> puzzles played, times, hints used, streaks, leaderboard entries.</li>
            <li><strong>Subscription state:</strong> whether you're on Free or Premium, and Stripe's customer &amp; subscription IDs.</li>
            <li><strong>Technical:</strong> IP address, browser type, and request logs (for security &amp; abuse prevention).</li>
            <li><strong>Optional social sign-in identifiers:</strong> Google <code>sub</code> or Apple <code>sub</code> if you sign in with those providers.</li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> collect location, contacts, biometric
            data, health data, or advertising identifiers. There are no ads
            in the app today.
          </p>
        </Section>

        <Section title="How we use it">
          <ul className="list-disc pl-6 space-y-1">
            <li>Signing you in and keeping you signed in (HttpOnly cookies).</li>
            <li>Saving your streaks, stats, and leaderboard position.</li>
            <li>Processing your Premium subscription through Stripe.</li>
            <li>Sending you password-reset emails via Resend when you request them.</li>
            <li>Detecting abuse, brute-force logins, and puzzle-generation exploits.</li>
          </ul>
        </Section>

        <Section title="Third parties we use">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Stripe</strong> — processes your Premium subscription.
              Stripe sees your name, email, card details, and country. We
              never see or store your card. See <a className="underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">Stripe's privacy policy</a>.
            </li>
            <li>
              <strong>Google Sign-In</strong> — if you use it, Google shares
              your Google ID, email, and display name with us.
            </li>
            <li>
              <strong>Sign in with Apple</strong> — if you use it, Apple
              shares a stable ID and (optionally) a private-relay email.
            </li>
            <li>
              <strong>Resend</strong> — delivers transactional email
              (password reset). Resend sees the destination email address
              and the message body.
            </li>
            <li>
              <strong>Google Analytics 4</strong> — aggregate, anonymized
              (<code>anonymize_ip</code>) usage analytics. No cross-site tracking.
            </li>
            <li>
              <strong>Microsoft Clarity</strong> — session recordings and
              heatmaps used to improve UX. Recordings are stripped of form
              inputs by default.
            </li>
            <li>
              <strong>PostHog</strong> — product analytics for identified
              users only. No anonymous person profiles are created.
            </li>
          </ul>
        </Section>

        <Section title="Cookies">
          <p>We use two kinds of cookies:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>
              <strong>Essential (HttpOnly):</strong> <code>access_token</code>{' '}
              and <code>refresh_token</code> for keeping you signed in.
              These are <code>SameSite=None; Secure</code> to allow the app
              to work under both the default host and the custom domain.
            </li>
            <li>
              <strong>Analytics:</strong> Google Analytics, Clarity, and
              PostHog set their own cookies. You can block these in your
              browser settings without breaking the game.
            </li>
          </ul>
        </Section>

        <Section title="Data retention">
          <p>
            We keep your account and gameplay data until you delete your
            account or ask us to. Deleted-account data is purged
            immediately from our primary database; backup snapshots roll
            off within 30 days. Anonymous aggregate stats (e.g. "how many
            people solved Level 7 this week") may be retained indefinitely.
          </p>
        </Section>

        <Section title="Your rights">
          <p>You can, at any time:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>
              <strong>Delete your account</strong> from{' '}
              <Link to="/dashboard" className="underline text-[#8B5A3C]">
                Dashboard → Danger Zone
              </Link>{' '}
              — this removes your user record, sessions, stats, streaks,
              leaderboard entries, referrals, and subscription reference.
            </li>
            <li>
              <strong>Export your gameplay data</strong> — email us at
              <a className="underline ml-1" href="mailto:privacy@sudokuacademy.org">
                privacy@sudokuacademy.org
              </a>{' '}
              and we'll send you a JSON dump within 30 days.
            </li>
            <li>
              <strong>Ask us to correct anything</strong> — same email.
            </li>
          </ul>
        </Section>

        <Section title="Children">
          <p>
            Sudoku Academy is intended for ages 13+. We don't knowingly
            collect data from children under 13. If you believe a child
            has created an account, email
            <a className="underline ml-1" href="mailto:privacy@sudokuacademy.org">
              privacy@sudokuacademy.org
            </a>{' '}
            and we'll delete it.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we make a material change, we'll update the "Last updated"
            date at the top and — if you have an account with an email on
            file — send you a notice. Continued use of the app after a
            change constitutes acceptance.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions?{' '}
            <a className="underline text-[#8B5A3C]" href="mailto:privacy@sudokuacademy.org">
              privacy@sudokuacademy.org
            </a>
          </p>
        </Section>

        <div className="mt-16 text-sm text-[var(--text-muted)]">
          <Link to="/support" className="underline hover:text-[#8B5A3C]" data-testid="privacy-to-support-link">
            Need help instead? Visit Support →
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-2xl font-heading font-semibold mb-3 text-[#3B2416]">
        {title}
      </h2>
      <div className="space-y-2 text-[var(--text-secondary)] leading-relaxed">
        {children}
      </div>
    </section>
  );
}
